import {
  ORDER_CLEAR_SELECTED_ITEMS,
  RECEIVE_ADD_NEW_BILLING_ADDRESS_ERROR,
  RECEIVE_ADD_NEW_PAYMENT_ERROR,
  RECEIVE_ADD_NEW_PAYMENT_SUCCESS,
  RECEIVE_BILLING_ADDRESSES,
  RECEIVE_CONFIRM_CANCELLATION,
  RECEIVE_CONFIRM_CANCELLATION_ERROR,
  RECEIVE_ORDER_DETAILS,
  RECEIVE_SUGGESTED_ADDRESSES,
  RECEIVE_UPDATE_PAYMENT_ERROR,
  RECEIVE_UPDATE_PAYMENT_SUCCESS,
  REDIRECT,
  REQUEST_ADD_NEW_BILLING_ADDRESS,
  REQUEST_ADD_NEW_PAYMENT,
  REQUEST_BILLING_ADDRESSES,
  REQUEST_CONFIRM_CANCELLATION,
  REQUEST_ORDER_DETAILS,
  REQUEST_UPDATE_ASSOCIATED_PAYMENT,
  REQUEST_UPDATE_UNASSOCIATED_PAYMENT,
  TOGGLE_ADD_NEW_BILLING_ADDRESS,
  UPDATE_CANCELLATION_REASON,
  UPDATE_ORDER_WITH_TRACKING_INFO
} from 'constants/reduxActions';

import { prependAppRoot } from 'history/AppRootUtils';
import { fetchShipmentTracking } from 'actions/account/shipmentTracking';
import { authenticationErrorCatchHandlerFactory } from 'actions/errors';
import { redirectTo } from 'actions/redirect';
import {
  fetchApiNotAuthenticatedMiddleware,
  fetchErrorMiddleware,
  fetchMafiaValidationErrorMiddleware
} from 'middleware/fetchErrorMiddleware';
import {
  addresses,
  cancelOrder,
  changeAddress,
  creditCard,
  getOrderV3,
  savePayment,
  updatePayment
} from 'apis/mafia';
import { setSessionCookies } from 'actions/session';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import marketplace from 'cfg/marketplace.json';

export function requestOrderDetails() {
  return {
    type: REQUEST_ORDER_DETAILS
  };
}

export function receiveOrderDetails(order) {
  return {
    type: RECEIVE_ORDER_DETAILS,
    order
  };
}

export function updateCancellationReason(cancellationReasonByLineItem) {
  return {
    type: UPDATE_CANCELLATION_REASON,
    cancellationReasonByLineItem
  };
}

export function clearSelectedItems() {
  return {
    type: ORDER_CLEAR_SELECTED_ITEMS
  };
}

export function requestConfirmCancellation() {
  return {
    type: REQUEST_CONFIRM_CANCELLATION
  };
}

export function receiveConfirmCancellation() {
  return {
    type: RECEIVE_CONFIRM_CANCELLATION
  };
}

export function receiveConfirmCancellationError() {
  return {
    type: RECEIVE_CONFIRM_CANCELLATION_ERROR
  };
}

export function requestUpdateAssociatedPayment() {
  return {
    type: REQUEST_UPDATE_ASSOCIATED_PAYMENT
  };
}

export function receiveUpdatePaymentSuccess() {
  return {
    type: RECEIVE_UPDATE_PAYMENT_SUCCESS
  };
}

export function requestUpdateUnassociatedPayment() {
  return {
    type: REQUEST_UPDATE_UNASSOCIATED_PAYMENT
  };
}

export function receiveUpdatePaymentError() {
  return {
    type: RECEIVE_UPDATE_PAYMENT_ERROR
  };
}

export function requestAddNewPayment() {
  return {
    type: REQUEST_ADD_NEW_PAYMENT
  };
}

export function receiveAddNewPaymentSuccess(newPaymentInstrumentId) {
  return {
    type: RECEIVE_ADD_NEW_PAYMENT_SUCCESS,
    newPaymentInstrumentId
  };
}

export function receiveAddNewPaymentError(error) {
  return {
    type: RECEIVE_ADD_NEW_PAYMENT_ERROR,
    error
  };
}

export function requestBillingAddresses() {
  return {
    type: REQUEST_BILLING_ADDRESSES
  };
}

export function receiveBillingAddresses(addresses) {
  return {
    type: RECEIVE_BILLING_ADDRESSES,
    addresses
  };
}

export function toggleAddNewBillingAddress(visible = false) {
  return {
    type: TOGGLE_ADD_NEW_BILLING_ADDRESS,
    visible
  };
}

export function requestAddNewBillingAddress() {
  return {
    type: REQUEST_ADD_NEW_BILLING_ADDRESS
  };
}

export function receiveAddNewBillingAddressError(error) {
  return {
    type: RECEIVE_ADD_NEW_BILLING_ADDRESS_ERROR,
    error
  };
}

export function receiveSuggestedAddresses(suggestedAddresses, newAddressId) {
  return {
    type: RECEIVE_SUGGESTED_ADDRESSES,
    suggestedAddresses,
    newAddressId
  };
}

export function updateOrderWithTrackingInfo(order, trackingInfo) {
  return {
    type: UPDATE_ORDER_WITH_TRACKING_INFO,
    order,
    trackingInfo
  };
}

export function confirmCancellation(orderId, items, cancelOrderEndpoint = cancelOrder) {
  return (dispatch, getState) => {
    dispatch(requestConfirmCancellation());
    const { cookies, environmentConfig: { api: { mafia } }, routing: { locationBeforeTransitions } } = getState();
    return cancelOrderEndpoint(mafia, { orderId, items }, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        if (resp.success) {
          dispatch(receiveConfirmCancellation(resp));
        } else {
          dispatch(receiveConfirmCancellationError(resp));
        }
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(`/orders/${orderId}`, locationBeforeTransitions)));
  };
}

export function fetchOrderDetails(orderId, shouldFetchTracking = false, getOrderEndpoint = getOrderV3) {
  return (dispatch, getState) => {
    dispatch(requestOrderDetails());
    const { cookies, environmentConfig: { api: { mafia } } } = getState();

    return getOrderEndpoint(mafia, orderId, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        const { orderId, shipments } = resp;
        if (shouldFetchTracking && orderId && shipments) { // fetch tracking info for mobile order detail page
          shipments.forEach(shipment => {
            const { id } = shipment;
            dispatch(fetchShipmentTracking(orderId, id));
          });
        }
        dispatch(receiveOrderDetails(resp));
        return resp;
      })
      .catch(() => { // if order call fails, redirect user back to main account page
        dispatch({ type: REDIRECT, location: '/account' });
      });
  };
}

export function updateAssociatedPayment(paymentInstrumentId, updatePaymentEndpoint = updatePayment) {
  return (dispatch, getState) => {
    dispatch(requestUpdateAssociatedPayment());
    const {
      cookies,
      environmentConfig: { api: { mafia } },
      routing: { locationBeforeTransitions },
      order: { order: { orderId } }
    } = getState();
    return updatePaymentEndpoint(mafia, { orderId, paymentInstrumentId }, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(({ result }) => {
        if (result) {
          dispatch(receiveUpdatePaymentSuccess());
        }
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(`/orders/${orderId}`, locationBeforeTransitions)));
  };
}

export function updateUnassociatedPayment(ccNum, paymentInstrumentId, addressId, creditCardEndpoint = creditCard) {
  return (dispatch, getState) => {
    dispatch(requestUpdateUnassociatedPayment());
    const { environmentConfig: { api: { mafia } }, order: { order: { orderId } } } = getState();
    return creditCardEndpoint(mafia, ccNum, paymentInstrumentId, addressId) // associate payment instrument
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMafiaValidationErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(({ status }) => {
        if (status) {
          dispatch(updateAssociatedPayment(orderId, paymentInstrumentId));
        } else { // something went wrong
          dispatch(receiveUpdatePaymentError());
        }
      })
      .catch(() => {
        dispatch(receiveUpdatePaymentError());
      });
  };
}

export function addNewPayment(stateInstrumentData, savePaymentEndpoint = savePayment) {
  return (dispatch, getState) => {
    dispatch(requestAddNewPayment());
    const { cookies, environmentConfig: { api: { mafia } } } = getState();
    return savePaymentEndpoint(mafia, stateInstrumentData, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMafiaValidationErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(resp => {
        const { paymentInstrumentId, response } = resp;
        if (paymentInstrumentId) { // successful 1st call that generates paymentInstrumentId
          dispatch(receiveAddNewPaymentSuccess(paymentInstrumentId));
          return resp; // for amethyst addPaymentInstrument event
        } else if (response) { // successful 2nd call with addressId ('response' key is paymentInstrumentId)
          dispatch(updateAssociatedPayment(response));
        } else { // something went wrong
          dispatch(receiveAddNewPaymentError());
        }
      })
      .catch(e => {
        dispatch(receiveAddNewPaymentError(e));
      });
  };
}

export function fetchBillingAddresses(addressesEndpoint = addresses) {
  return (dispatch, getState) => {
    dispatch(requestBillingAddresses());
    const {
      cookies,
      environmentConfig: { api: { mafia } },
      routing: { locationBeforeTransitions },
      order: { order: { orderId } }
    } = getState();
    return addressesEndpoint(mafia, cookies)
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(({ addresses }) => {
        dispatch(receiveBillingAddresses(addresses));
      })
      .catch(authenticationErrorCatchHandlerFactory(dispatch, prependAppRoot(`/orders/${orderId}`, locationBeforeTransitions)));
  };
}

export function saveNewBillingAddress(stateAddressData, ccStateInstrumentData, suggestedAddresses, changeAddressEndpoint = changeAddress) {
  return (dispatch, getState) => {
    dispatch(requestAddNewBillingAddress());
    const {
      cookies,
      environmentConfig: { api: { mafia } },
      order: { newPaymentInstrumentId }
    } = getState();
    const params = {
      mafia,
      address: stateAddressData,
      forceOriginal: !!suggestedAddresses // force address assuming user has already been shown suggestions
    };
    return changeAddressEndpoint(params, cookies, false) // saveInvalidAddress 3rd param set to false
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchMafiaValidationErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        const { valid, addressId, suggestedAddresses, invalidFields } = response;
        if (!valid) {
          if (invalidFields?.length) { // mafia api validation error
            dispatch(receiveAddNewBillingAddressError({ extraInformation: invalidFields }));
          }
          if (suggestedAddresses?.length) { // display suggested addresses
            dispatch(receiveSuggestedAddresses(suggestedAddresses, addressId));
          }
        } else if (addressId) { // assume everything looks good, addNew Payment
          const updatedInstrumentData = { // create deep copy to be safe
            ...ccStateInstrumentData,
            instrument: { ...ccStateInstrumentData.instrument }
          };
          updatedInstrumentData.addressId = addressId;
          updatedInstrumentData.instrument.paymentInstrumentId = newPaymentInstrumentId;
          dispatch(addNewPayment(updatedInstrumentData));
        }
        return response;
      })
      .catch(e => {
        dispatch(receiveAddNewBillingAddressError(e));
      });
  };
}

export function redirectToLegacyReturns(productsList) {
  return (dispatch, getState) => {
    const { order: { order: { orderId } } } = getState();
    const { desktopBaseUrl } = marketplace;
    return dispatch(redirectTo(`${desktopBaseUrl}/returnInitiate?orderId=${orderId}&useComet=true${productsList}`));
  };
}
